import type { Location } from "react-router-dom";
import { ExploreType, routes } from "@/common/utils/routes";
import { FeedSection, IFeedVisit } from "@/common/store/feedResumeStore";

interface IFeedResumeState {
  resumeScrollFrom: string;
}

export type FeedLinkProps = {
  state?: IFeedResumeState;
  replace?: boolean;
};

export type FeedNavigation =
  | { kind: "closePlayer"; link?: undefined; linkProps?: undefined }
  | { kind: "startOver" | "resume"; link: string; linkProps?: FeedLinkProps };

interface IFeedNavigationParams {
  section: FeedSection;
  location: Pick<Location, "pathname" | "search">;
  visit?: IFeedVisit;
  defaultPath: string;
  isPlayerOpen: boolean;
}

const EXPLORE_PATHS = Object.values(ExploreType).map((type) => routes.explore(type));

export const getFeedSection = (pathname: string): FeedSection | null => {
  if (pathname === routes.home()) {
    return FeedSection.home;
  }

  if (EXPLORE_PATHS.includes(pathname)) {
    return FeedSection.explore;
  }

  return null;
};

export const getFeedPath = (pathname: string, search: string): string => {
  const order = new URLSearchParams(search).get("order");

  return order ? `${pathname}?${new URLSearchParams({ order })}` : pathname;
};

export const getResumeScrollFrom = (state: unknown): string | null => {
  if (typeof state !== "object" || state === null || !("resumeScrollFrom" in state)) {
    return null;
  }

  const { resumeScrollFrom } = state;

  return typeof resumeScrollFrom === "string" ? resumeScrollFrom : null;
};

export const getFeedNavigation = ({ section, location, visit, defaultPath, isPlayerOpen }: IFeedNavigationParams): FeedNavigation => {
  const isOnFeed = getFeedSection(location.pathname) === section;

  if (isOnFeed && isPlayerOpen) {
    return { kind: "closePlayer" };
  }

  if (isOnFeed) {
    return { kind: "startOver", link: getFeedPath(location.pathname, location.search), linkProps: { replace: true } };
  }

  return {
    kind: "resume",
    link: visit?.path ?? defaultPath,
    linkProps: visit && { state: { resumeScrollFrom: visit.locationKey } },
  };
};
